import { useForm } from '@inertiajs/react'
import StudentLayout from '../components/StudentLayout'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import Button from '@arianna/design-system/Button'
import Card from '@arianna/design-system/Card'

interface SubParagraph {
  id: string
  title: string
  rationale?: string
}

interface SubTheme {
  id: string
  title: string
  rationale?: string
  subParagraphs: SubParagraph[]
}

interface Chapter {
  id: string
  title: string
  rationale?: string
  subThemes: SubTheme[]
}

interface Toc {
  title?: string
  subtitle?: string | null
  author?: string
  rationale?: string
  chapters?: Chapter[]
}

interface ManualInfo {
  id: number
  slug: string
  title: string
  author: string
}

interface Props {
  manual: ManualInfo
  toc: Toc
}

export default function Preview({ manual, toc }: Props) {
  const form = useForm({})

  function handleEnroll(e: React.FormEvent) {
    e.preventDefault()
    // The server responds with a 303 redirect to /read/:manual_version_id.
    // Inertia follows the redirect automatically and lands on the Reader page.
    // No manual_version_id extraction needed here.
    form.post(`/manuals/${manual.id}/enroll`)
  }

  const chapters = toc.chapters ?? []

  return (
    <StudentLayout>
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
          padding: 'var(--space-10) var(--space-6)',
        }}
      >
        {/* Back link */}
        <a
          href="/library"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--ink-3)',
            textDecoration: 'none',
            fontSize: 13,
            fontFamily: 'var(--font-sans)',
            marginBottom: 'var(--space-6)',
          }}
        >
          ← Libreria
        </a>

        {/* Header */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <Heading variant="eyebrow" style={{ marginBottom: 'var(--space-2)' }}>
            Anteprima
          </Heading>
          <Heading variant="panel" style={{ marginBottom: 'var(--space-2)' }}>
            {manual.title}
          </Heading>
          <Text variant="body" style={{ color: 'var(--ink-3)', display: 'block' }}>
            {manual.author}
          </Text>
          {toc.subtitle && (
            <Text
              variant="meta"
              style={{
                color: 'var(--ink-3)',
                display: 'block',
                marginTop: 'var(--space-1)',
                fontSize: 13,
              }}
            >
              {toc.subtitle}
            </Text>
          )}
        </div>

        {/* Rationale */}
        {toc.rationale && (
          <Card
            padding="md"
            style={{ marginBottom: 'var(--space-6)', borderLeft: '3px solid var(--accent)' }}
          >
            <Text variant="body" style={{ color: 'var(--ink-2)', fontStyle: 'italic' }}>
              {toc.rationale}
            </Text>
          </Card>
        )}

        {/* Studia CTA */}
        <form onSubmit={handleEnroll} style={{ marginBottom: 'var(--space-8)' }}>
          <Button type="submit" variant="accent" size="lg" disabled={form.processing}>
            {form.processing ? 'Avvio in corso…' : 'Inizia a studiare'}
          </Button>
        </form>

        {/* TOC */}
        {chapters.length > 0 ? (
          <div>
            <Heading variant="h3" style={{ marginBottom: 'var(--space-4)' }}>
              Indice dei contenuti
            </Heading>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {chapters.map((chapter) => (
                <Card key={chapter.id} padding="md">
                  <Heading
                    variant="cardSm"
                    style={{
                      marginBottom: chapter.rationale ? 'var(--space-2)' : 'var(--space-3)',
                    }}
                  >
                    {chapter.title}
                  </Heading>
                  {chapter.rationale && (
                    <Text
                      variant="meta"
                      style={{
                        color: 'var(--ink-3)',
                        display: 'block',
                        fontStyle: 'italic',
                        fontSize: 13,
                        marginBottom: 'var(--space-3)',
                      }}
                    >
                      {chapter.rationale}
                    </Text>
                  )}
                  {chapter.subThemes.map((st) => (
                    <div
                      key={st.id}
                      style={{ paddingLeft: 'var(--space-4)', marginBottom: 'var(--space-3)' }}
                    >
                      <Text
                        variant="meta"
                        style={{
                          fontWeight: 600,
                          color: 'var(--ink-2)',
                          display: 'block',
                          fontSize: 13,
                        }}
                      >
                        {st.title}
                      </Text>
                      {st.rationale && (
                        <Text
                          variant="meta"
                          style={{
                            color: 'var(--ink-4)',
                            display: 'block',
                            fontStyle: 'italic',
                            fontSize: 12,
                            margin: 'var(--space-1) 0 var(--space-2)',
                          }}
                        >
                          {st.rationale}
                        </Text>
                      )}
                      {/* Tailwind v4 preflight resets list-style: use div instead of ul */}
                      <div style={{ paddingLeft: 'var(--space-4)', marginTop: 'var(--space-1)' }}>
                        {st.subParagraphs.map((sp) => (
                          <div
                            key={sp.id}
                            style={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 'var(--space-2)',
                              marginBottom: 'var(--space-1)',
                            }}
                          >
                            <span style={{ color: 'var(--ink-4)', fontSize: 10, flexShrink: 0 }}>
                              ·
                            </span>
                            <Text variant="meta" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                              {sp.title}
                              {sp.rationale && (
                                <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>
                                  {' '}
                                  &mdash; {sp.rationale}
                                </span>
                              )}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <Text variant="body" style={{ color: 'var(--ink-4)' }}>
            Indice non ancora disponibile (ingest in corso).
          </Text>
        )}
      </div>
    </StudentLayout>
  )
}
